import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useWebSocket } from '../../contexts/WebSocketContext'
import { apiUrl } from '../../config/api'
import ChessBoard from '../Chess/ChessBoard'
import UserDisplay from '../UserDisplay/UserDisplay'
import './SpectatorGameView.css'

const PIECE_SYMBOLS = {
  'K': '♔', 'Q': '♕', 'R': '♖', 'B': '♗', 'N': '♘', 'P': '♙',
  'k': '♚', 'q': '♛', 'r': '♜', 'b': '♝', 'n': '♞', 'p': '♟'
}

// Load piece images for captured pieces display
const loadPieceImages = (theme) => {
  try {
    if (theme === 'cyberpunk') {
      return {
        'K': require('../../assets/images/pieces/cyberpunk/kingwhite.png'),
        'Q': require('../../assets/images/pieces/cyberpunk/queenwhite.png'),
        'R': require('../../assets/images/pieces/cyberpunk/rookwhite.png'),
        'B': require('../../assets/images/pieces/cyberpunk/bishopwhite.png'),
        'N': require('../../assets/images/pieces/cyberpunk/knightwhite.png'),
        'P': require('../../assets/images/pieces/cyberpunk/pawnwhite.png'),
        'k': require('../../assets/images/pieces/cyberpunk/kingblack.png'),
        'q': require('../../assets/images/pieces/cyberpunk/queenblack.png'),
        'r': require('../../assets/images/pieces/cyberpunk/rookblack.png'),
        'b': require('../../assets/images/pieces/cyberpunk/bishopblack.png'),
        'n': require('../../assets/images/pieces/cyberpunk/knightblack.png'),
        'p': require('../../assets/images/pieces/cyberpunk/pawnblack.png')
      }
    }
    if (theme === '2') {
      return {
        'K': require('../../assets/images/pieces/2/whiteking.png'),
        'Q': require('../../assets/images/pieces/2/whitequeen.png'),
        'R': require('../../assets/images/pieces/2/whiterook.png'),
        'B': require('../../assets/images/pieces/2/whitebishop.png'),
        'N': require('../../assets/images/pieces/2/whiteknight.png'),
        'P': require('../../assets/images/pieces/2/whitepawn.png'),
        'k': require('../../assets/images/pieces/2/blackking.png'),
        'q': require('../../assets/images/pieces/2/blackqueen.png'),
        'r': require('../../assets/images/pieces/2/blackrook.png'),
        'b': require('../../assets/images/pieces/2/blackbishop.png'),
        'n': require('../../assets/images/pieces/2/blackknight.png'),
        'p': require('../../assets/images/pieces/2/blackpawn.png')
      }
    }
    if (theme === '3') {
      return {
        'K': require('../../assets/images/pieces/3/whiteking.png'),
        'Q': require('../../assets/images/pieces/3/whitequeen.png'),
        'R': require('../../assets/images/pieces/3/whiterook.png'),
        'B': require('../../assets/images/pieces/3/whitebishop.png'),
        'N': require('../../assets/images/pieces/3/whiteknight.png'),
        'P': require('../../assets/images/pieces/3/whitepawn.png'),
        'k': require('../../assets/images/pieces/3/blackking.png'),
        'q': require('../../assets/images/pieces/3/blackqueen.png'),
        'r': require('../../assets/images/pieces/3/blackrook.png'),
        'b': require('../../assets/images/pieces/3/blackbishop.png'),
        'n': require('../../assets/images/pieces/3/blackknight.png'),
        'p': require('../../assets/images/pieces/3/blackpawn.png')
      }
    }
    // Default to classic theme
    return {
      'K': require('../../assets/images/pieces/classic/white-king.png'),
      'Q': require('../../assets/images/pieces/classic/white-queen.png'),
      'R': require('../../assets/images/pieces/classic/white-rook.png'),
      'B': require('../../assets/images/pieces/classic/white-bishop.png'),
      'N': require('../../assets/images/pieces/classic/white-knight.png'),
      'P': require('../../assets/images/pieces/classic/white-pawn.png'),
      'k': require('../../assets/images/pieces/classic/black-king.png'),
      'q': require('../../assets/images/pieces/classic/black-queen.png'),
      'r': require('../../assets/images/pieces/classic/black-rook.png'),
      'b': require('../../assets/images/pieces/classic/black-bishop.png'),
      'n': require('../../assets/images/pieces/classic/black-knight.png'),
      'p': require('../../assets/images/pieces/classic/black-pawn.png')
    }
  } catch (error) {
    console.error('Error loading piece images:', error)
    return {}
  }
}

// Component to display a piece image
const PieceImage = ({ piece, theme, className }) => {
  const pieceImages = useMemo(() => loadPieceImages(theme), [theme])
  const imageSrc = pieceImages[piece]

  if (!imageSrc) {
    // Fallback to symbol if image not found
    return <span className={className}>{PIECE_SYMBOLS[piece] || '?'}</span>
  }

  return (
    <img
      src={imageSrc}
      alt={piece}
      className={className}
      draggable={false}
    />
  )
}

const SpectatorGameView = ({ gameId, onBack }) => {
  const { user, session } = useAuth()
  const { spectateGame, leaveSpectate, addMessageHandler, removeMessageHandler, isConnected } = useWebSocket()
  const [game, setGame] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [gameEnded, setGameEnded] = useState(false)
  const [gameResult, setGameResult] = useState(null)
  const [currentTime, setCurrentTime] = useState(Date.now())
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now())

  // Real-time timer countdown
  useEffect(() => {
    if (!game || game.status !== 'active') return

    const interval = setInterval(() => {
      setCurrentTime(Date.now())
    }, 100) // Update every 100ms for smooth countdown

    return () => clearInterval(interval)
  }, [game?.status])

  // Note: Removed API fetch - now using WebSocket-only initialization
  // Game data will be loaded when first WebSocket game_state message arrives

  // Handle WebSocket game state updates
  const handleGameStateMessage = useCallback((message) => {
    if (message.type === 'game_state' && message.game_id === gameId) {
      const gameData = message.data.game || message.data
      setGame(gameData)
      setLoading(false)
      setError(null)
    }
  }, [gameId])

  // Handle WebSocket move updates for real-time spectating
  const handleMoveUpdate = useCallback((message) => {
    if (message.type === 'move_update' && message.game_id === gameId) {
      // Update last update time for timer countdown
      setLastUpdateTime(Date.now())

      // Update game state with new move data
      setGame(prevGame => ({
        ...prevGame,
        current_board: message.current_board,
        current_turn: message.current_turn,
        move_count: message.move_count,
        white_time_remaining: message.white_time_remaining,
        black_time_remaining: message.black_time_remaining,
        status: message.status,
        last_move: message.move,
        result: message.result,
        red_squares: message.red_squares,
        blue_squares: message.blue_squares,
        green_squares: message.green_squares,
        visual_effects: message.visual_effects,
      }))
    }
  }, [gameId])

  // Handle game completion events
  const handleGameCompletion = useCallback((message) => {
    if (message.type === 'game_completed' && message.game_id === gameId) {
      const { data } = message

      // Set game as ended and store result
      setGameEnded(true)
      setGameResult(data.result)

      // Update the game state with final status
      setGame(prevGame => ({
        ...prevGame,
        status: 'ended',
        result: data.result
      }))

      // Auto-redirect back to spectate list after 5 seconds
      setTimeout(() => {
        onBack()
      }, 5000)
    }
  }, [gameId, onBack])

  // Note: Removed initial API fetch - now relying on WebSocket for all game data

  useEffect(() => {
    let isSubscribed = true

    // Add WebSocket message handlers
    addMessageHandler('game_state', handleGameStateMessage)
    addMessageHandler('move_update', handleMoveUpdate)
    addMessageHandler('game_completed', handleGameCompletion)

    // Initial fetch of game data
    const fetchGameData = async () => {
      try {
        const response = await fetch(apiUrl(`games/${gameId}`), {
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
        })
        if (response.ok) {
          const data = await response.json()
          if (isSubscribed) {
            setGame(data)
            setLoading(false)
          }
        } else {
             // If fetch fails, we still try WS, but it might mean 404
             console.error("Failed to fetch game snapshot")
        }
      } catch (err) {
         console.error("Error fetching game:", err)
      }
    }

    if (session?.access_token) {
        fetchGameData()
    }

    // Join spectating when component mounts
    // Add a small delay to prevent race condition with leaveSpectate from previous component
    // (e.g. when navigating from Dashboard where LiveGamesPreview just unmounted)
    let spectateTimer = null
    
    if (gameId && isConnected && isSubscribed) {
      spectateTimer = setTimeout(() => {
        spectateGame(gameId)
          .then(() => {
            // If we haven't fetched data yet, we wait for WS or fetch
          })
          .catch(err => {
            console.error('Failed to spectate game:', err)
            if (isSubscribed) {
              // Only set error if we also failed to fetch (loading is still true)
            }
          })
      }, 500)
    }

    return () => {
      isSubscribed = false
      if (spectateTimer) clearTimeout(spectateTimer)
      
      // Clean up: leave spectating and remove message handlers
      removeMessageHandler('game_state')
      removeMessageHandler('move_update')
      removeMessageHandler('game_completed')
      if (isConnected) {
        leaveSpectate()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, isConnected, session?.access_token])

  // Timer countdown effect
  useEffect(() => {
    if (!game || game.status === 'ended') return

    const timer = setInterval(() => {
      setCurrentTime(Date.now())
    }, 1000)

    return () => clearInterval(timer)
  }, [game])

  const calculateCapturedPieces = (initialBoard, currentBoard) => {
    const initial = {}
    const current = {}

    // Count pieces in initial position
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = initialBoard[row][col]
        if (piece) {
          initial[piece] = (initial[piece] || 0) + 1
        }
      }
    }

    // Count pieces in current position
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = currentBoard[row][col]
        if (piece) {
          current[piece] = (current[piece] || 0) + 1
        }
      }
    }

    // Calculate captured pieces
    const captured = { white: [], black: [] }

    Object.keys(initial).forEach(piece => {
      const initialCount = initial[piece]
      const currentCount = current[piece] || 0
      const capturedCount = initialCount - currentCount

      for (let i = 0; i < capturedCount; i++) {
        if (piece === piece.toUpperCase()) {
          captured.black.push(piece) // White piece captured by black
        } else {
          captured.white.push(piece) // Black piece captured by white
        }
      }
    })

    return captured
  }

  const formatTime = (timeInSeconds, isCurrentPlayer) => {
    if (typeof timeInSeconds !== 'number' || isNaN(timeInSeconds)) {
      return '--:--'
    }

    // Calculate elapsed time since last update for live countdown - only for active player
    let adjustedTime = timeInSeconds
    if (game && game.status === 'active' && isCurrentPlayer && lastUpdateTime) {
      const elapsedSeconds = (currentTime - lastUpdateTime) / 1000
      adjustedTime = Math.max(0, timeInSeconds - elapsedSeconds)
    }

    const minutes = Math.floor(Math.abs(adjustedTime) / 60)
    const seconds = Math.floor(Math.abs(adjustedTime) % 60)

    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="spectator-game-view">
        <div className="loading">Loading game...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="spectator-game-view">
        <div className="error">{error}</div>
        <button onClick={onBack} className="back-button">Back</button>
      </div>
    )
  }

  if (!game) {
    return (
      <div className="spectator-game-view">
        <div className="error">Game not found</div>
        <button onClick={onBack} className="back-button">Back</button>
      </div>
    )
  }

  const capturedPieces = game.initial_board ? calculateCapturedPieces(game.initial_board, game.current_board) : { white: [], black: [] }
  const userTheme = user?.themes?.piece_theme || 'classic'

  return (
    <div className="spectator-game-view">
      <div className="game-header">
        <button onClick={onBack} className="back-button">← Back</button>
      </div>

      {/* Game End Notification */}
      {gameEnded && (
        <div className="game-end-notification">
          <div className="notification-content">
            <h3>Game Ended!</h3>
            <p className="result-text">
              {gameResult === 'white_wins' && '⚪ White wins!'}
              {gameResult === 'black_wins' && '⚫ Black wins!'}
              {gameResult === 'draw' && '🤝 Game drawn!'}
              {gameResult === 'stalemate' && '🤝 Stalemate!'}
              {gameResult === 'threefold_repetition' && '🔄 Draw by repetition'}
              {gameResult === 'insufficient_material' && '🤝 Insufficient material'}
              {(gameResult === 'white_timeout' || gameResult === 'black_timeout') && '⏰ Time out!'}
            </p>
            <p className="redirect-text">
              Redirecting to games list in 5 seconds...
            </p>
            <button onClick={onBack} className="back-now-button">
              Back to Games List Now
            </button>
          </div>
        </div>
      )}

      <div className="game-layout">
        {/* Left Panel - Player Info and Captured Pieces */}
        <div className="game-sidebar left">
          {/* Black Player */}
          <div className="player-section black">
            <div className="player-info">
              <div className="color-indicator black-indicator">⚫</div>
              <div className="player-details">
                <div className="player-name-rating">
                  <span className="player-rating">({game.black_player?.rating || '?'})</span>
                  <UserDisplay
                    user={game.black_player || { username: `Player ${game.black_player_id?.substring(0, 8) || 'Unknown'}` }}
                    color="black"
                    isCurrentTurn={game.current_turn === 'black'}
                  />
                </div>
                <div className={`player-time ${game.current_turn === 'black' ? 'active-timer' : ''}`}>
                  {formatTime(game.black_time_remaining, game.current_turn === 'black')}
                </div>
              </div>
            </div>
            {/* White's captured pieces (captured by black) */}
            <div className="captured-pieces">
              {capturedPieces.black.map((piece, index) => (
                <PieceImage
                  key={index}
                  piece={piece}
                  theme={userTheme}
                  className="captured-piece"
                />
              ))}
            </div>
          </div>

        </div>

        {/* Center - Chess Board */}
        <div className="game-board-container">
          <div className="spectator-page-badge">Spectating</div>
          <ChessBoard
            game={{
              ...game,
              board: game.current_board || game.board
            }}
            onSquareClick={() => {}} // No interaction for spectators
            selectedSquare={null}
            legalMoves={[]}
            lastMove={game.last_move}
            isPlayerWhite={true} // Default orientation
            disabled={true} // Disable all interactions
          />
        </div>

        {/* Right Panel - Game Info */}
        <div className="game-sidebar right">
          {/* White Player */}
          <div className="player-section white">
            <div className="player-info">
              <div className="color-indicator white-indicator">⚪</div>
              <div className="player-details">
                <div className="player-name-rating">
                  <span className="player-rating">({game.white_player?.rating || '?'})</span>
                  <UserDisplay
                    user={game.white_player || { username: `Player ${game.white_player_id?.substring(0, 8) || 'Unknown'}` }}
                    color="white"
                    isCurrentTurn={game.current_turn === 'white'}
                  />
                </div>
                <div className={`player-time ${game.current_turn === 'white' ? 'active-timer' : ''}`}>
                  {formatTime(game.white_time_remaining, game.current_turn === 'white')}
                </div>
              </div>
            </div>
            {/* Black's captured pieces (captured by white) */}
            <div className="captured-pieces">
              {capturedPieces.white.map((piece, index) => (
                <PieceImage
                  key={index}
                  piece={piece}
                  theme={userTheme}
                  className="captured-piece"
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

export default SpectatorGameView